import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { API, BACKEND_URL } from '../../App';
import { coverUrl, COVER_WIDTH } from '../../lib/coverUrl';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, Send, Loader2, Plus, Trash2, Pencil, CheckCircle2, Image as ImageIcon } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const EMPTY_ENTRY = {
  hoofstuk: '',
  bladsy: '',
  paragraaf: '',
  sin: '',
  fout_tipe: '',
  fout_kategorie: 'Oorspronklik',
  oorspronklike_teks: '',
  voorgestelde_regstelling: '',
  nota: '',
  screenshot_url: '',
};

const hasAnyContent = (e) =>
  !!(e.hoofstuk || e.bladsy || e.paragraaf || e.sin || e.fout_tipe ||
     e.oorspronklike_teks || e.voorgestelde_regstelling || e.nota || e.screenshot_url);

const isComplete = (e) =>
  !!(e.bladsy && e.paragraaf && e.sin && e.fout_tipe && e.fout_kategorie &&
     e.oorspronklike_teks && e.voorgestelde_regstelling);

const CurrentWork = () => {
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);

  // The "form at the top" — single fault being edited right now
  const [currentEntry, setCurrentEntry] = useState({ ...EMPTY_ENTRY });
  // Faults already added to the list (shown in the table below)
  const [savedEntries, setSavedEntries] = useState([]);
  // When the user clicks "Edit" on a saved row, we remember the index so we can
  // re-insert at the same position on "Voeg Fout By".
  const [editingIndex, setEditingIndex] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [markingNoFaults, setMarkingNoFaults] = useState(false);
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  // Trigger a re-render after mount so we can detect the AdminProofreading slot div.
  const [, forceTick] = useState(0);
  useEffect(() => { forceTick((t) => t + 1); }, []);

  // Persists the draft list whenever savedEntries changes (after init).
  const initLoadedRef = useRef(false);
  const persistDraft = useCallback(async (entries, bookId) => {
    if (!bookId) return;
    setSavingDraft(true);
    try {
      await axios.put(`${API}/proofreading/draft/${bookId}`, { fault_entries: entries });
    } catch (error) {
      // Non-fatal: keep the local state, just warn the user once.
      console.error('Draft save failed:', error);
    } finally {
      setSavingDraft(false);
    }
  }, []);

  const handleDownloadBook = () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Nie gemagtig nie');
        return;
      }
      const url = `${API}/files/book/${book.id}?version=original&download=1&token=${token}`;
      window.open(url, '_blank');
      toast.success('Aflaai begin... Kyk in jou Downloads gids');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Kon nie boek aflaai nie');
    }
  };

  const fetchCurrentWork = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/proofreading/current-work`);
      if (response.data && response.data.id) {
        setBook(response.data);
        // Load any in-progress draft
        try {
          const dr = await axios.get(`${API}/proofreading/draft/${response.data.id}`);
          const entries = (dr.data && dr.data.fault_entries) || [];
          if (entries.length > 0) {
            setSavedEntries(entries);
            toast.info(`${entries.length} bestaande foute herstel uit jou vorige sessie`);
          }
        } catch (err) {
          // Draft is optional — ignore failures so the page still loads.
          console.warn('Draft load failed:', err);
        }
      }
    } catch (error) {
      console.error('Failed to fetch current work:', error);
    } finally {
      setLoading(false);
      initLoadedRef.current = true;
    }
  }, []);

  useEffect(() => {
    fetchCurrentWork();
  }, [fetchCurrentWork]);

  // Persist whenever the saved list changes (skip the initial load).
  useEffect(() => {
    if (!initLoadedRef.current || !book?.id) return;
    persistDraft(savedEntries, book.id);
  }, [savedEntries, book?.id, persistDraft]);

  // "Voeg Fout by Huidige Posisie" from the reader pane — auto-commits any
  // half-typed entry first, then prefills a fresh one with bladsy/hoofstuk/text.
  useEffect(() => {
    const handler = (e) => {
      const { bladsy = '', hoofstuk = '', oorspronklike_teks = '' } = e.detail || {};
      setCurrentEntry((prev) => {
        // If the current entry has nothing in it, just prefill.
        if (!hasAnyContent(prev)) {
          return { ...EMPTY_ENTRY, hoofstuk, bladsy, oorspronklike_teks };
        }
        // Otherwise: if it's a complete fault, commit it and prefill.
        if (isComplete(prev)) {
          setSavedEntries((list) => [...list, prev]);
          return { ...EMPTY_ENTRY, hoofstuk, bladsy, oorspronklike_teks };
        }
        // Partial → don't lose the user's text; just merge new location info on top.
        return { ...prev, hoofstuk: hoofstuk || prev.hoofstuk, bladsy: bladsy || prev.bladsy,
                 oorspronklike_teks: oorspronklike_teks || prev.oorspronklike_teks };
      });
    };
    window.addEventListener('proofreader-prefill-fault', handler);
    return () => window.removeEventListener('proofreader-prefill-fault', handler);
  }, []);

  const updateField = (field, value) =>
    setCurrentEntry((prev) => ({ ...prev, [field]: value }));

  const handleScreenshotUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Slegs beelde word aanvaar');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Beeld moet kleiner as 5MB wees');
      return;
    }
    setUploadingScreenshot(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', 'ml_default');
      const response = await axios.post(`${API}/upload/screenshot`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateField('screenshot_url', response.data.url);
      toast.success('Skermkiekie opgelaai');
    } catch (error) {
      console.error('Screenshot upload failed:', error);
      toast.error('Kon nie skermkiekie oplaai nie');
    } finally {
      setUploadingScreenshot(false);
    }
  };

  // "Voeg Fout By" — validate the current entry, push to savedEntries, reset form.
  const commitCurrentEntry = () => {
    if (!isComplete(currentEntry)) {
      toast.error('Voltooi alle verpligte velde (gemerk met *) voordat jy die fout byvoeg');
      return;
    }
    setSavedEntries((list) => {
      if (editingIndex !== null) {
        const next = [...list];
        next.splice(editingIndex, 0, currentEntry);
        return next;
      }
      return [...list, currentEntry];
    });
    setEditingIndex(null);
    setCurrentEntry({ ...EMPTY_ENTRY });
    toast.success(`Fout #${savedEntries.length + 1} bygevoeg`);
  };

  const editRow = (index) => {
    // If the editor has unfinished content, push it first so we don't lose it.
    if (hasAnyContent(currentEntry)) {
      if (!isComplete(currentEntry)) {
        toast.error('Voltooi of maak die huidige fout skoon voordat jy \'n bestaande fout wysig');
        return;
      }
      setSavedEntries((list) => [...list, currentEntry]);
    }
    setCurrentEntry({ ...savedEntries[index] });
    setSavedEntries((list) => list.filter((_, i) => i !== index));
    setEditingIndex(index);
  };

  const deleteRow = (index) => {
    setSavedEntries((list) => list.filter((_, i) => i !== index));
    if (editingIndex !== null && editingIndex >= index) {
      setEditingIndex(editingIndex === index ? null : editingIndex - 1);
    }
  };

  const clearCurrentEntry = () => {
    setCurrentEntry({ ...EMPTY_ENTRY });
    setEditingIndex(null);
  };

  const handleMarkNoFaults = async () => {
    if (!book?.id) return;
    setMarkingNoFaults(true);
    try {
      const r = await axios.post(`${API}/proofreading/mark-no-faults/${book.id}`);
      toast.success(r.data?.message || 'Boek gemerk as geen foute - wag vir admin goedkeuring');
      // Clear any draft so the next book starts fresh
      try { await axios.delete(`${API}/proofreading/draft/${book.id}`); } catch (e) { /* ignore */ }
      setBook(null);
      setSavedEntries([]);
      setCurrentEntry({ ...EMPTY_ENTRY });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie boek as geen foute merk nie');
    } finally {
      setMarkingNoFaults(false);
    }
  };

  const handleSubmit = async () => {
    // If the editor has content, auto-commit before submitting (one-button submit).
    let allEntries = savedEntries;
    if (hasAnyContent(currentEntry)) {
      if (!isComplete(currentEntry)) {
        toast.error('Voltooi die huidige fout of maak dit skoon voor indiening');
        return;
      }
      allEntries = [...savedEntries, currentEntry];
    }
    if (allEntries.length === 0) {
      toast.error('Voeg ten minste een fout by, of klik "Geen Foute Gevind"');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/proofreading/submit-fault-list`, {
        book_id: book.id,
        fault_entries: allEntries,
      });
      toast.success('Foute lys suksesvol ingedien!');
      // Backend clears the draft, but be defensive client-side too.
      setBook(null);
      setSavedEntries([]);
      setCurrentEntry({ ...EMPTY_ENTRY });
      setEditingIndex(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Indiening het misluk');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  }

  if (!book) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-gray-500 text-lg mb-4">Jy het nie &apos;n boek gekies nie</p>
          <Button onClick={() => window.location.href = '/proofreader/available'}>Kies &apos;n Boek</Button>
        </CardContent>
      </Card>
    );
  }

  const currentFaultNumber = savedEntries.length + 1;

  // Saved-faults table — rendered EITHER inline (standalone proofreader page)
  // OR portaled into the AdminProofreading layout's left column (below the reader)
  // when the slot div is present.
  const savedFaultsTable = savedEntries.length > 0 ? (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center justify-between">
          <span>Bygevoegde Foute ({savedEntries.length})</span>
          <span className="text-xs font-normal text-gray-500">
            Klik op &lsquo;n ry om te wysig
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm" data-testid="saved-faults-table">
            <thead>
              <tr className="bg-slate-50 border-y text-left text-gray-700">
                <th className="py-2 px-2 font-semibold w-10">#</th>
                <th className="py-2 px-2 font-semibold">Hoofstuk</th>
                <th className="py-2 px-2 font-semibold w-12">Bls</th>
                <th className="py-2 px-2 font-semibold w-14">Par</th>
                <th className="py-2 px-2 font-semibold w-12">Sin</th>
                <th className="py-2 px-2 font-semibold">Tipe</th>
                <th className="py-2 px-2 font-semibold">Kategorie</th>
                <th className="py-2 px-2 font-semibold">Oorspronklike Teks</th>
                <th className="py-2 px-2 font-semibold">Voorgestelde Regstelling</th>
                <th className="py-2 px-2 font-semibold">Nota</th>
                <th className="py-2 px-2 font-semibold w-24 text-right">Aksies</th>
              </tr>
            </thead>
            <tbody>
              {savedEntries.map((e, idx) => (
                <tr
                  key={`saved-${idx}-${e.bladsy}-${e.paragraaf}-${e.sin}`}
                  className="border-b hover:bg-orange-50 cursor-pointer align-top"
                  onClick={() => editRow(idx)}
                  data-testid={`saved-fault-row-${idx}`}
                >
                  <td className="py-2 px-2 font-bold text-orange-700">{idx + 1}</td>
                  <td className="py-2 px-2">{e.hoofstuk || '-'}</td>
                  <td className="py-2 px-2">{e.bladsy}</td>
                  <td className="py-2 px-2">{e.paragraaf}</td>
                  <td className="py-2 px-2">{e.sin}</td>
                  <td className="py-2 px-2">{e.fout_tipe}</td>
                  <td className="py-2 px-2">{e.fout_kategorie}</td>
                  <td className="py-2 px-2 max-w-[260px] break-words text-red-700">{e.oorspronklike_teks}</td>
                  <td className="py-2 px-2 max-w-[260px] break-words text-emerald-700">{e.voorgestelde_regstelling}</td>
                  <td className="py-2 px-2 max-w-[260px] break-words text-gray-600">{e.nota || '-'}</td>
                  <td className="py-2 px-2 text-right whitespace-nowrap" onClick={(ev) => ev.stopPropagation()}>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      onClick={() => editRow(idx)}
                      data-testid={`edit-fault-${idx}`}
                      aria-label="Wysig"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => deleteRow(idx)}
                      data-testid={`delete-fault-${idx}`}
                      aria-label="Verwyder"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  ) : null;

  // Find the slot — if AdminProofreading rendered one, portal there; else render inline.
  const slotEl = typeof document !== 'undefined'
    ? document.getElementById('proofreader-fault-list-slot')
    : null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Huidige Werk</h2>
        <p className="text-gray-600">Proeflees die gekose boek</p>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 mb-2">
            <img src={coverUrl(book.id, COVER_WIDTH.default)} alt={book.boek_naam} loading="lazy" width={128} height={192} className="w-32 h-48 object-cover rounded shadow flex-shrink-0 mx-auto sm:mx-0" />
            <div className="flex-1 min-w-0">
              <h3 className="text-2xl font-bold text-gray-900 mb-1 break-words">{book.boek_naam}</h3>
              <p className="text-gray-600 mb-4">deur {book.skrywer_naam}</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="min-w-0"><p className="text-gray-500">Genre</p><p className="font-semibold break-words">{Array.isArray(book.genre) ? book.genre.join(', ') : book.genre}</p></div>
                <div className="min-w-0"><p className="text-gray-500">Woorde</p><p className="font-semibold">{book.word_count?.toLocaleString()}</p></div>
                <div className="min-w-0"><p className="text-gray-500">Bladsye</p><p className="font-semibold">{book.page_count}</p></div>
                <div className="min-w-0"><p className="text-gray-500">Betaling</p><p className="font-semibold text-green-600">R2 per fout</p></div>
              </div>
              <Button
                data-testid="download-book-btn"
                variant="outline"
                className="mt-4"
                onClick={handleDownloadBook}
              >
                <Download className="mr-2 h-4 w-4" />Laai Boek Af
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-lg">
              {editingIndex !== null ? 'Wysig Fout' : `Fout #${currentFaultNumber}`}
            </CardTitle>
            <span className="text-xs text-gray-500 flex items-center gap-2">
              {savingDraft && <><Loader2 className="h-3 w-3 animate-spin" /> Stoor tans...</>}
              {!savingDraft && savedEntries.length > 0 && (
                <>
                  <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                  Lys outomaties gestoor — {savedEntries.length} {savedEntries.length === 1 ? 'fout' : 'foute'} tot dusver
                </>
              )}
            </span>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Location fields */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="min-w-0">
              <Label className="text-sm">Hoofstuk</Label>
              <Input
                type="text"
                value={currentEntry.hoofstuk}
                onChange={(e) => updateField('hoofstuk', e.target.value)}
                placeholder="bv. Hoofstuk 3"
                data-testid="fault-chapter"
              />
            </div>
            <div className="min-w-0">
              <Label className="text-sm">Bladsy *</Label>
              <Input
                type="number"
                value={currentEntry.bladsy}
                onChange={(e) => updateField('bladsy', e.target.value)}
                placeholder="bv. 5"
                data-testid="fault-page"
              />
            </div>
            <div className="min-w-0">
              <Label className="text-sm">Paragraaf *</Label>
              <Input
                type="number"
                value={currentEntry.paragraaf}
                onChange={(e) => updateField('paragraaf', e.target.value)}
                placeholder="bv. 2"
                data-testid="fault-paragraph"
              />
            </div>
            <div className="min-w-0">
              <Label className="text-sm">Sin *</Label>
              <Input
                type="number"
                value={currentEntry.sin}
                onChange={(e) => updateField('sin', e.target.value)}
                placeholder="bv. 3"
                data-testid="fault-sentence"
              />
            </div>
          </div>

          {/* Error classification */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Fout Tipe *</Label>
              <Select
                value={currentEntry.fout_tipe}
                onValueChange={(v) => updateField('fout_tipe', v)}
              >
                <SelectTrigger data-testid="fault-type">
                  <SelectValue placeholder="Kies tipe" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Spelling">Spelling</SelectItem>
                  <SelectItem value="Grammatika">Grammatika</SelectItem>
                  <SelectItem value="Leestekens">Leestekens (Punctuation)</SelectItem>
                  <SelectItem value="Woordkeuse">Woordkeuse</SelectItem>
                  <SelectItem value="Sinsbou">Sinsbou (Sentence Structure)</SelectItem>
                  <SelectItem value="Inkonsekwent">Inkonsekwent (Inconsistency)</SelectItem>
                  <SelectItem value="Ander">Ander</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fout Kategorie *</Label>
              <Select
                value={currentEntry.fout_kategorie}
                onValueChange={(v) => updateField('fout_kategorie', v)}
              >
                <SelectTrigger data-testid="fault-category">
                  <SelectValue placeholder="Kies kategorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Oorspronklik">Oorspronklike Skrywer Fout</SelectItem>
                  <SelectItem value="Vertaling">Vertaling Fout</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Text fields */}
          <div className="space-y-3">
            <div>
              <Label>Oorspronklike Teks *</Label>
              <Textarea
                value={currentEntry.oorspronklike_teks}
                onChange={(e) => updateField('oorspronklike_teks', e.target.value)}
                rows={2}
                placeholder="Tik die oorspronklike teks met die fout hier..."
                data-testid="fault-original"
              />
            </div>
            <div>
              <Label>Voorgestelde Regstelling *</Label>
              <Textarea
                value={currentEntry.voorgestelde_regstelling}
                onChange={(e) => updateField('voorgestelde_regstelling', e.target.value)}
                rows={2}
                placeholder="Tik jou voorgestelde regstelling hier..."
                data-testid="fault-correction"
              />
            </div>
            <div>
              <Label>Nota / Verduideliking</Label>
              <Textarea
                value={currentEntry.nota}
                onChange={(e) => updateField('nota', e.target.value)}
                rows={2}
                placeholder="Enige addisionele notas of verduideliking..."
                data-testid="fault-note"
              />
            </div>
          </div>

          {/* Optional screenshot */}
          <div className="border-t pt-3">
            <Label className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4" />
              Skermkiekie (Opsioneel)
            </Label>
            {currentEntry.screenshot_url ? (
              <div className="flex items-center gap-4 mt-2">
                <img src={currentEntry.screenshot_url} alt="Screenshot" className="h-20 w-auto rounded border" />
                <Button variant="outline" size="sm" onClick={() => updateField('screenshot_url', '')}>
                  <Trash2 className="h-4 w-4 mr-1" /> Verwyder
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleScreenshotUpload(e.target.files[0])}
                  disabled={uploadingScreenshot}
                  className="max-w-xs"
                  data-testid="fault-screenshot"
                />
                {uploadingScreenshot && <Loader2 className="h-4 w-4 animate-spin" />}
              </div>
            )}
          </div>

          {/* Action row */}
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              onClick={commitCurrentEntry}
              className="flex-1 min-w-[140px] bg-orange-500 hover:bg-orange-600"
              disabled={submitting || markingNoFaults}
              data-testid="add-fault-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              {editingIndex !== null ? 'Stoor Wysiging' : 'Voeg Fout By'}
            </Button>
            {hasAnyContent(currentEntry) && (
              <Button
                variant="ghost"
                onClick={clearCurrentEntry}
                disabled={submitting || markingNoFaults}
                data-testid="clear-fault-btn"
              >
                Maak Skoon
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Saved faults table — portal to AdminProofreading slot when present, else render inline */}
      {savedFaultsTable && (slotEl ? ReactDOM.createPortal(savedFaultsTable, slotEl) : savedFaultsTable)}

      {/* No-faults shortcut */}
      <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="font-semibold text-emerald-900 text-sm">
                Geen foute in die boek gevind?
              </p>
              <p className="text-xs text-emerald-700">
                Stuur die boek direk na admin vir finale goedkeuring en publikasie.
              </p>
            </div>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="border-emerald-500 text-emerald-700 hover:bg-emerald-100 flex-shrink-0"
                disabled={markingNoFaults || submitting || savedEntries.length > 0}
                data-testid="mark-no-faults-btn"
              >
                {markingNoFaults ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Besig...</>
                ) : (
                  <><CheckCircle2 className="mr-2 h-4 w-4" />Geen Foute Gevind</>
                )}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Bevestig: Geen Foute Gevind</AlertDialogTitle>
                <AlertDialogDescription>
                  Is jy seker daar is geen foute in <strong>&ldquo;{book.boek_naam}&rdquo;</strong> nie?
                  Die boek sal direk na <strong>&ldquo;Wag vir Goedkeuring&rdquo;</strong> gestuur word vir
                  finale admin goedkeuring.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="mark-no-faults-cancel">Kanselleer</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleMarkNoFaults}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="mark-no-faults-confirm"
                >
                  Ja, stuur na goedkeuring
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Submit */}
      <Button
        data-testid="submit-fault-list-btn"
        onClick={handleSubmit}
        disabled={submitting || markingNoFaults || (savedEntries.length === 0 && !isComplete(currentEntry))}
        className="w-full bg-orange-600 hover:bg-orange-700"
      >
        {submitting ? (
          <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Besig om in te dien...</>
        ) : (
          <><Send className="mr-2 h-4 w-4" />Dien Foute Lys In
            {savedEntries.length > 0 && ` (${savedEntries.length}${hasAnyContent(currentEntry) ? '+1' : ''})`}
          </>
        )}
      </Button>
    </div>
  );
};

export default CurrentWork;
